import * as React from 'react';
import { cn } from '@/shared/utils/styling';
import { Input } from '@/shared/components/ui/input';
import { CountrySelectInput, ErrorText } from '@/shared/components';
import type { Country } from '@/shared/types';
import type { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type PhoneSelectInputProps = {
  countryCodeValue?: string;
  phoneNumberValue?: string;
  onCountryCodeChange?: (countryCode: string) => void;
  onPhoneNumberChange?: (phoneNumber: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  countries: Country[];
  errorCountryCode?: FieldError;
  errorPhoneNumber?: FieldError;
};

function PhoneSelectInput({
  countryCodeValue,
  phoneNumberValue,
  onCountryCodeChange,
  onPhoneNumberChange,
  placeholder,
  className,
  disabled = false,
  countries,
  errorCountryCode,
  errorPhoneNumber,
}: PhoneSelectInputProps) {
  const { t } = useTranslation();
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const number = e.target.value;
    onPhoneNumberChange?.(number);
  };

  return (
    <div
      className={cn('grid grid-cols-[auto_1fr] gap-4 items-start', className)}>
      <div className="flex flex-col">
        <CountrySelectInput
          value={countryCodeValue}
          onValueChange={onCountryCodeChange}
          countries={countries}
          error={errorCountryCode}
          disabled={disabled}
          placeholder={t('common.code')}
          valueProperty="phoneCode"
          displayProperty="phoneCode"
          listItemDisplayProperty="name"
          listItemSecondaryProperty="phoneCode"
          buttonClassName="justify-between h-[6rem] min-w-[10rem] rounded-3xl"
          searchPlaceholder={t('common.search')}
        />
      </div>

      <div className="flex flex-col">
        <Input
          type="tel"
          label={placeholder}
          value={phoneNumberValue}
          onChange={handlePhoneNumberChange}
          className="p3 rounded-3xl w-full h-[6rem]"
          disabled={disabled}
          aria-invalid={!!errorPhoneNumber}
        />
        {errorPhoneNumber && (
          <ErrorText size="small" className="mt-2">
            {errorPhoneNumber.message}
          </ErrorText>
        )}
      </div>
    </div>
  );
}

export { PhoneSelectInput };
